import axios from "axios";
import urlMap from "@/api/url";

export type OAuthAuthorizeQuery = {
  client_id: string;
  response_type: string;
  redirect_uri: string;
  scope: string;
  state: string;
  code_challenge: string;
  code_challenge_method: string;
};

export type OAuthAuthorizeContext = {
  transaction_id: string;
  client_id: string;
  client_name: string;
  redirect_uri: string;
  scopes: string[];
};

type LegacyTokens = {
  access_token?: { access_token?: string } | string;
};

type OAuthProviderURL = {
  url?: string;
};

export async function getAuthorizeContext(
  query: OAuthAuthorizeQuery
): Promise<OAuthAuthorizeContext> {
  const response = await axios.get(urlMap.oauthAuthorizeContext, {
    params: query,
    timeout: 10000,
  });
  return unwrap<OAuthAuthorizeContext>(response.data);
}

export async function completeAuthorization(
  transactionID: string,
  accessToken: string
): Promise<{ redirect_url: string }> {
  const response = await axios.post(
    urlMap.oauthAuthorizeComplete,
    { transaction_id: transactionID },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      timeout: 10000,
    }
  );
  return unwrap<{ redirect_url: string }>(response.data);
}

export async function sendOAuthPhoneCode(phone: string): Promise<void> {
  await axios.post(urlMap.sendSMS, { phone: phone.trim() }, { timeout: 10000 });
}

export async function loginOAuthByPhone(
  phone: string,
  code: string
): Promise<string> {
  const response = await axios.post(
    urlMap.phoneSignIn,
    { phone: phone.trim(), code: code.trim() },
    { timeout: 10000 }
  );
  return readAccessToken(unwrap<LegacyTokens>(response.data));
}

export async function loginOAuthByEmail(
  email: string,
  password: string
): Promise<string> {
  const response = await axios.post(
    urlMap.emailSignIn,
    { email: email.trim(), password },
    { timeout: 10000 }
  );
  return readAccessToken(unwrap<LegacyTokens>(response.data));
}

export async function getOAuthProviderURL(
  channel: string,
  redirectURL: string
): Promise<string> {
  const response = await axios.get(urlMap.oauth2Url, {
    params: { channel, redirect_url: redirectURL },
    timeout: 10000,
  });
  const payload = unwrap<OAuthProviderURL>(response.data);
  if (!payload.url) {
    throw new Error("认证服务没有返回第三方登录地址");
  }
  return payload.url;
}

export async function completeOAuthProvider(
  channel: string,
  code: string
): Promise<string> {
  const response = await axios.post(
    urlMap.oauth2SignIn,
    { channel, code },
    { timeout: 15000 }
  );
  return readAccessToken(unwrap<LegacyTokens>(response.data));
}

export function getOAuthErrorMessage(cause: unknown): string {
  if (axios.isAxiosError(cause)) {
    const data = cause.response?.data as
      | {
          error_message?: string;
          message?: string;
          error_data?: Array<{ content?: string }>;
        }
      | undefined;
    const detail = data?.error_data?.find((item) =>
      item.content?.trim()
    )?.content;
    return detail || data?.error_message || data?.message || cause.message;
  }
  if (cause instanceof Error && cause.message.trim()) {
    return cause.message;
  }
  if (typeof cause === "string" && cause.trim()) {
    return cause;
  }
  return "登录失败";
}

function readAccessToken(payload: LegacyTokens): string {
  const value = payload.access_token;
  const token = typeof value === "string" ? value : value?.access_token;
  if (!token?.trim()) {
    throw new Error("认证服务没有返回 access token");
  }
  return token.trim();
}

function unwrap<T>(value: unknown): T {
  if (value && typeof value === "object" && "data" in value) {
    const record = value as { data?: unknown };
    if (Object.keys(value).length === 1 && record.data !== undefined) {
      return record.data as T;
    }
  }
  return value as T;
}
